import React from "react";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import {
  Archive,
  ArrowCounterClockwise,
  ArrowsLeftRight,
  Copy,
  PencilLine,
  Trash,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TargetActionMenuProps {
  /** The element that anchors the menu — typically the overflow IconButton */
  anchorEl: HTMLElement | null;
  /** Whether the menu is open */
  open: boolean;
  /** Called when the menu should close (backdrop click, Escape key, item click) */
  onClose: () => void;
  /** Called when "Edit Target" is selected */
  onEdit: () => void;
  /**
   * Called when "Duplicate Target" is selected.
   * When omitted, the Duplicate item is hidden (e.g. on TargetDetail header menu).
   */
  onDuplicate?: () => void;
  /** Called when "Move to Goal" is selected */
  onMove: () => void;
  /** Called when "Archive Target" is selected */
  onArchive: () => void;
  /** Called when "Unarchive Target" is selected — shown instead of Archive when `isArchived` */
  onUnarchive?: () => void;
  /** Whether the target is archived — drives the Archive/Unarchive toggle */
  isArchived?: boolean;
  /** Called when "Delete Target" is selected */
  onDelete: () => void;
}

// ---------------------------------------------------------------------------
// TargetActionMenu
// ---------------------------------------------------------------------------

/**
 * Overflow action menu for a single target.
 *
 * @param props
 * @param props.anchorEl    - DOM element that anchors the popover position
 * @param props.open        - Whether the menu is visible
 * @param props.onClose     - Called on backdrop click or Escape key
 * @param props.onEdit      - Handler for "Edit Target"
 * @param [props.onDuplicate] - Handler for "Duplicate Target". When omitted, the item is hidden.
 * @param props.onMove      - Handler for "Move to Goal"
 * @param props.onArchive   - Handler for "Archive Target"
 * @param [props.onUnarchive] - Handler for "Unarchive Target", shown instead of Archive when `isArchived`
 * @param [props.isArchived]  - Whether the target is archived (drives the Archive/Unarchive toggle)
 * @param props.onDelete    - Handler for "Delete Target"
 */
const TargetActionMenu: React.FC<TargetActionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onEdit,
  onDuplicate,
  onMove,
  onArchive,
  onUnarchive,
  isArchived = false,
  onDelete,
}) => {
  const handleItem = (callback: () => void) => () => {
    callback();
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: { elevation: 3 },
      }}
    >
      <MenuItem onClick={handleItem(onEdit)} aria-label="Edit target">
        <ListItemIcon>
          <PencilLine size={20} />
        </ListItemIcon>
        <ListItemText>
          <Typography component="p" variant="body2">
            Edit Target
          </Typography>
        </ListItemText>
      </MenuItem>

      {onDuplicate && (
        <MenuItem onClick={handleItem(onDuplicate)} aria-label="Duplicate target">
          <ListItemIcon>
            <Copy size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography component="p" variant="body2">
              Duplicate Target
            </Typography>
          </ListItemText>
        </MenuItem>
      )}

      <MenuItem onClick={handleItem(onMove)} aria-label="Move target to another goal">
        <ListItemIcon>
          <ArrowsLeftRight size={20} />
        </ListItemIcon>
        <ListItemText>
          <Typography component="p" variant="body2">
            Move to Goal
          </Typography>
        </ListItemText>
      </MenuItem>

      {isArchived && onUnarchive ? (
        <MenuItem onClick={handleItem(onUnarchive)} aria-label="Unarchive target">
          <ListItemIcon>
            <ArrowCounterClockwise size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography component="p" variant="body2">
              Unarchive Target
            </Typography>
          </ListItemText>
        </MenuItem>
      ) : (
        <MenuItem onClick={handleItem(onArchive)} aria-label="Archive target">
          <ListItemIcon>
            <Archive size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography component="p" variant="body2">
              Archive Target
            </Typography>
          </ListItemText>
        </MenuItem>
      )}

      <Divider />

      <MenuItem
        onClick={handleItem(onDelete)}
        aria-label="Delete target"
        sx={{ color: "error.main" }}
      >
        <ListItemIcon sx={{ color: "inherit" }}>
          <Trash size={20} color="#D32F2F" />
        </ListItemIcon>
        <ListItemText>
          <Box component={"p"} sx={{ color: "error.main", fontSize: 14, margin: 0 }}>
            Delete Target
          </Box>
        </ListItemText>
      </MenuItem>
    </Menu>
  );
};

export default TargetActionMenu;
